/**
 * How a damage type reads: its name, its colour, and how an unknown one is handled.
 *
 * Pure and Babylon-free, because the same answer is needed in two places that share
 * nothing else — a DOM floating number and a Babylon projectile material — which is
 * the split `models/poses.ts` and `game/bolt.ts` have for the same reason.
 *
 * Typed damage is otherwise invisible to a player. The number that appears has
 * already been mitigated by the server, so a reduced fire hit and an ordinary hit
 * are the same digits; without the colour there is no way to learn which resistance
 * a zone is asking for.
 */

import { isDamageType, type DamageType } from '../net/protocol';

/**
 * The type an event carries, with the wire's default filled in.
 *
 * Absent means `PHYSICAL` — the same convention `crit` uses for "no" — and an
 * **unrecognised** type means physical too, rather than falling through to some
 * fifth colour. A client older than the server is the case that produces one, and
 * an ordinary-looking hit is a better answer than a colourless one.
 */
export function damageTypeOf(raw: string | undefined): DamageType {
  if (raw === undefined || raw === '') return 'PHYSICAL';
  return isDamageType(raw) ? raw : 'PHYSICAL';
}

/**
 * The colour a type is drawn in, as a hex string.
 *
 * Declared here rather than in `tokens.css` because half the consumers are not CSS:
 * a Babylon `StandardMaterial` needs the number, and a token would have to be read
 * back out of the cascade at runtime. The floating number uses a matching custom
 * property so the two are authored side by side — `damage.test.ts` holds them
 * together.
 *
 * Physical is deliberately the *absence* of a colour: it is the overwhelming
 * majority of hits, and tinting it would make the elemental ones stop standing out.
 * The overlay reads `null` as "leave it the default gold or red".
 */
export function damageTypeColour(kind: DamageType): string | null {
  switch (kind) {
    case 'FIRE':
      return '#ff7a2f';
    case 'COLD':
      return '#5fd2ff';
    case 'LIGHTNING':
      return '#ffe14d';
    case 'VOID':
      return '#b86bff';
    default:
      return null;
  }
}

/**
 * The one-word label shown beside an elemental number.
 *
 * Colour alone is not enough: about one player in twelve cannot separate the fire
 * and lightning hues reliably, and both sit over the same near-black floor. Physical
 * has no label for the reason it has no colour.
 */
export function damageTypeLabel(kind: DamageType): string | null {
  switch (kind) {
    case 'FIRE':
      return 'Fire';
    case 'COLD':
      return 'Cold';
    case 'LIGHTNING':
      return 'Lightning';
    case 'VOID':
      return 'Void';
    default:
      return null;
  }
}

/**
 * The CSS modifier a floating number gets, or null for physical.
 *
 * A class rather than an inline colour so the stylesheet stays the one place a
 * rendered colour is chosen, and so the two halves of a number — the digits and the
 * label — cannot drift apart.
 */
export function damageTypeClass(kind: DamageType): string | null {
  return kind === 'PHYSICAL' ? null : `damage-number--${kind.toLowerCase()}`;
}
